import { useState } from "react"
import { Link } from "@inertiajs/react"
import type { SocialPost } from "@/types"
import PlatformIcon from "@/components/dashboard/platform-icon"
import { PLATFORM_LABELS, STATUS_STYLES, POST_TYPE_LABELS } from "@/lib/social-post-constants"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

interface SocialPostDetailDialogProps {
  post: SocialPost | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onDelete: (post: SocialPost) => void | Promise<void>
  onRetry: (post: SocialPost) => void
  showVideoLink?: boolean
}

export default function SocialPostDetailDialog({
  post,
  open,
  onOpenChange,
  onDelete,
  onRetry,
  showVideoLink = true,
}: SocialPostDetailDialogProps) {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  if (!post) return null

  const statusStyle = STATUS_STYLES[post.status] || STATUS_STYLES.pending
  const isPublished = post.status === "published"
  const canRetry = post.status === "failed"

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlatformIcon platform={post.platform} className="size-5 shrink-0" />
              {PLATFORM_LABELS[post.platform] || post.platform}
              <Badge variant="outline" className="ml-1 text-[10px] border-stone/20 text-warm-grey">
                {POST_TYPE_LABELS[post.post_type] || post.post_type}
              </Badge>
              <Badge variant="outline" className={`text-[10px] ${statusStyle.className}`}>
                {post.status === "uploading" && (
                  <svg className="mr-1 size-3 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                {statusStyle.label}
              </Badge>
            </DialogTitle>
          </DialogHeader>

          <div className="-mx-6 flex-1 space-y-4 overflow-y-auto px-6">
            {/* Media preview */}
            {post.video_file_url ? (
              <video
                src={post.video_file_url}
                controls
                className="max-h-48 rounded-lg border border-stone/20"
              />
            ) : post.video_thumbnail_url ? (
              <div className="group relative w-fit">
                <img
                  src={post.video_thumbnail_url}
                  alt="Video thumbnail"
                  className="max-h-40 rounded-lg border border-stone/20 object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/0 transition-colors group-hover:bg-black/10">
                  <div className="flex size-8 items-center justify-center rounded-full bg-white/90 shadow">
                    <svg className="ml-0.5 size-3.5 text-charcoal" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5.14v14l11-7-11-7z" />
                    </svg>
                  </div>
                </div>
              </div>
            ) : post.image_url ? (
              <img
                src={post.image_url}
                alt="Post image"
                className="max-h-40 rounded-lg border border-stone/20 object-cover"
              />
            ) : null}

            {/* Listing */}
            {post.listing_address && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-warm-grey">Listing</p>
                {post.listing_slug ? (
                  <Link
                    href={`/dashboard/listings/${post.listing_slug}`}
                    className="text-sm font-medium text-charcoal hover:text-red"
                  >
                    {post.listing_address}
                  </Link>
                ) : showVideoLink && post.video_id ? (
                  <Link
                    href={`/dashboard/videos/${post.video_id}`}
                    className="text-sm font-medium text-charcoal hover:text-red"
                  >
                    {post.listing_address}
                  </Link>
                ) : (
                  <p className="text-sm text-charcoal">{post.listing_address}</p>
                )}
              </div>
            )}

            {/* Video name — only show for video posts */}
            {post.video_name && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-warm-grey">Video</p>
                <p className="text-sm text-charcoal">{post.video_name}</p>
              </div>
            )}

            {/* Location */}
            {post.location_id && post.location_name && (() => {
              const meta = post.location_meta as { address?: string; verified?: boolean; picture_url?: string; hours?: string } | undefined
              return (
                <LocationDetail
                  name={post.location_name}
                  address={meta?.address}
                  verified={meta?.verified}
                  pictureUrl={meta?.picture_url}
                  hours={meta?.hours}
                />
              )
            })()}

            {/* Caption */}
            {post.caption && (
              <div className="rounded-lg border border-stone/15 bg-white p-3">
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-charcoal">{post.caption}</p>
              </div>
            )}

            {/* Dates */}
            <div className="flex flex-wrap gap-x-4 gap-y-1.5">
              <DateChip label="Created" date={post.created_at} />
              {post.scheduled_at && <DateChip label="Scheduled" date={post.scheduled_at} />}
              {post.published_at && <DateChip label="Published" date={post.published_at} />}
            </div>

            {/* Metrics */}
            {post.status === "published" && <MetricsGrid metrics={post.metrics} />}

            {/* Error */}
            {post.status === "failed" && post.error_message && (
              <div className="rounded-md bg-red/5 px-3 py-2">
                <p className="text-xs font-medium text-red">Error</p>
                <p className="mt-0.5 text-sm text-red/80">{post.error_message}</p>
              </div>
            )}
          </div>

          {/* Footer actions */}
          <div className="flex items-center gap-2 border-t border-stone/15 pt-4">
            {post.status === "published" && post.platform_post_url && (
              <Button variant="outline" size="sm" asChild>
                <a href={post.platform_post_url} target="_blank" rel="noopener noreferrer">
                  View on {PLATFORM_LABELS[post.platform] || post.platform}
                </a>
              </Button>
            )}
            {showVideoLink && post.video_id && (
              <Button variant="outline" size="sm" asChild>
                <Link href={`/dashboard/videos/${post.video_id}`}>View video</Link>
              </Button>
            )}
            {!post.video_id && post.listing_slug && (
              <Button variant="outline" size="sm" asChild>
                <Link href={`/dashboard/listings/${post.listing_slug}`}>View listing</Link>
              </Button>
            )}
            <div className="flex-1" />
            {canRetry && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onRetry(post)
                  onOpenChange(false)
                }}
              >
                Retry
              </Button>
            )}
            <Button variant="destructive" size="sm" onClick={() => setConfirmDelete(true)} disabled={deleting}>
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete post</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  {isPublished
                    ? post.platform === "instagram"
                      ? "This will remove the post from QuiteRight. This action cannot be undone."
                      : `This will delete the post from ${PLATFORM_LABELS[post.platform] || "the platform"} and remove it from QuiteRight. This action cannot be undone.`
                    : `Are you sure you want to delete this ${PLATFORM_LABELS[post.platform] || ""} post? This action cannot be undone.`}
                </p>
                {isPublished && post.platform === "instagram" && (
                  <p className="text-xs text-charcoal/50">
                    Note: Instagram does not allow posts to be deleted via their API. You'll need to delete it directly from Instagram if you want it removed there too.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={deleting}
              onClick={async (e) => {
                e.preventDefault()
                setDeleting(true)
                try {
                  await onDelete(post)
                } finally {
                  setDeleting(false)
                  setConfirmDelete(false)
                  onOpenChange(false)
                }
              }}
            >
              {deleting ? "Deleting..." : "Delete post"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

// ---------------------------------------------------------------------------
// Date chip — small inline with calendar icon
// ---------------------------------------------------------------------------

function DateChip({ label, date }: { label: string; date: string | Date }) {
  return (
    <span className="flex items-center gap-1.5 text-[11px] text-warm-grey">
      <svg className="size-3 text-warm-grey/50" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
      </svg>
      <span className="font-medium text-warm-grey/70">{label}</span>
      {formatDate(date)}
    </span>
  )
}

function formatDate(date: string | Date): string {
  return new Date(date).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

// ---------------------------------------------------------------------------
// Metrics grid — coloured icons, card-style chips
// ---------------------------------------------------------------------------

type Metrics = {
  views?: number
  likes?: number
  comments?: number
  shares?: number
  reach?: number
  saves?: number
  fetched_at?: string
}

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}m`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`
  return String(n)
}

const METRIC_CONFIG = [
  {
    key: "views" as const,
    label: "Views",
    hex: "#C41E2A",
    bg: "bg-[#C41E2A]/5",
    icon: (cls: string) => (
      <svg className={cls} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
      </svg>
    ),
  },
  {
    key: "likes" as const,
    label: "Likes",
    hex: "#D4A853",
    bg: "bg-[#D4A853]/5",
    icon: (cls: string) => (
      <svg className={cls} viewBox="0 0 24 24" fill="currentColor">
        <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 0 1-.383-.218 25.18 25.18 0 0 1-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0 1 12 5.052 5.5 5.5 0 0 1 16.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 0 1-4.244 3.17 15.247 15.247 0 0 1-.383.219l-.022.012-.007.004-.003.001a.752.752 0 0 1-.704 0l-.003-.001Z" />
      </svg>
    ),
  },
  {
    key: "comments" as const,
    label: "Comments",
    hex: "#3A3A3C",
    bg: "bg-[#3A3A3C]/5",
    icon: (cls: string) => (
      <svg className={cls} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 0 1-.923 1.785A5.969 5.969 0 0 0 6 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337Z" />
      </svg>
    ),
  },
  {
    key: "shares" as const,
    label: "Shares",
    hex: "#8E8E93",
    bg: "bg-[#8E8E93]/5",
    icon: (cls: string) => (
      <svg className={cls} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" />
      </svg>
    ),
  },
]

function MetricsGrid({ metrics }: { metrics?: Metrics | Record<string, unknown> }) {
  if (!metrics || typeof metrics !== "object") return null

  const m = metrics as Metrics
  const hasAny =
    (m.views ?? 0) > 0 ||
    (m.likes ?? 0) > 0 ||
    (m.comments ?? 0) > 0 ||
    (m.shares ?? 0) > 0

  if (!hasAny) {
    return (
      <p className="text-[11px] text-warm-grey/40">
        Metrics not yet available
      </p>
    )
  }

  const visible = METRIC_CONFIG.filter((mc) => ((m[mc.key] as number) ?? 0) > 0)

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-2">
        {visible.map((mc) => (
          <span
            key={mc.key}
            className={`inline-flex items-center gap-1.5 rounded-full ${mc.bg} px-2.5 py-1`}
          >
            <span className="size-3.5 shrink-0" style={{ color: mc.hex }}>{mc.icon("size-3.5")}</span>
            <span className="text-xs font-semibold tabular-nums" style={{ color: mc.hex }}>
              {formatCompact(m[mc.key]!)}
            </span>
            <span className="text-[10px] text-warm-grey">{mc.label}</span>
          </span>
        ))}
      </div>
      {m.fetched_at && (
        <p className="text-[10px] text-warm-grey/40">
          Updated {timeAgo(m.fetched_at)}
        </p>
      )}
    </div>
  )
}

function timeAgo(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime()
  const hours = Math.floor(diff / 3_600_000)
  if (hours < 1) return "just now"
  if (hours === 1) return "1h ago"
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days === 1) return "1d ago"
  return `${days}d ago`
}

// ---------------------------------------------------------------------------
// Location detail — shows tagged location with Facebook metadata
// ---------------------------------------------------------------------------

function LocationDetail({
  name,
  address,
  verified,
  pictureUrl,
  hours,
}: {
  name: string
  address?: string
  verified?: boolean
  pictureUrl?: string
  hours?: string
}) {
  return (
    <div className="rounded-lg border border-stone/15 bg-stone/5 p-3">
      <p className="mb-2 text-xs font-medium uppercase tracking-wider text-warm-grey">Location</p>
      <div className="flex items-start gap-2.5">
        {pictureUrl ? (
          <img
            src={pictureUrl}
            alt=""
            className="mt-0.5 size-8 shrink-0 rounded-full border border-stone/20 object-cover"
          />
        ) : (
          <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-stone/10">
            <svg className="size-4 text-warm-grey/50" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
            </svg>
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-charcoal">{name}</span>
            {verified && (
              <svg className="size-3.5 shrink-0 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1.41 15.06l-4.24-4.24 1.41-1.41 2.83 2.83 6.36-6.36 1.41 1.41-7.77 7.77z" />
              </svg>
            )}
          </div>
          {address && <p className="text-[11px] text-warm-grey">{address}</p>}
          {hours && <p className="text-[11px] text-warm-grey/70">{hours}</p>}
          <p className="mt-1 text-[10px] text-warm-grey/50">Source: Facebook Page public information</p>
        </div>
      </div>
    </div>
  )
}

